/**
 * 粒子效果管理器
 * 处理吸附成功、完成等特效
 */
import { Vector2, ParticleEffectType } from '../types';

// PixiJS 类型声明
declare const PIXI: any;

export interface ParticleConfig {
    count: number;           // 粒子数量
    lifetime: number;        // 生命周期（毫秒）
    speed: number;           // 速度
    size: number;            // 大小
    color: number;           // 颜色
    alpha: number;           // 透明度
    gravity: number;         // 重力
    spread: number;          // 扩散角度
}

interface Particle {
    sprite: any;             // PIXI.Sprite
    vx: number;              // X 速度
    vy: number;              // Y 速度
    life: number;            // 剩余生命
    maxLife: number;         // 最大生命
    gravity: number;         // 重力
    fadeRate: number;        // 衰减速率
}

export class ParticleManager {
    private container: any;  // PIXI.Container
    private particles: Particle[] = [];
    private edgeGlows: Array<{ sprite: any; life: number; maxLife: number }> = [];
    private particlePool: any[] = [];  // 对象池
    private glowSpritePool: any[] = []; // 光效对象池
    private glowTexture: any = null;    // 光效纹理
    private isRunning: boolean = false;
    private app: any;

    // 默认配置
    private defaultConfigs: Record<ParticleEffectType, ParticleConfig> = {
        [ParticleEffectType.SnapSuccess]: {
            count: 20,
            lifetime: 600,
            speed: 3,
            size: 4,
            color: 0x00ff88,
            alpha: 1,
            gravity: 0.1,
            spread: 360
        },
        [ParticleEffectType.PieceComplete]: {
            count: 30,
            lifetime: 800,
            speed: 5,
            size: 6,
            color: 0xffdd00,
            alpha: 1,
            gravity: 0.15,
            spread: 360
        },
        [ParticleEffectType.GameComplete]: {
            count: 100,
            lifetime: 2000,
            speed: 8,
            size: 8,
            color: 0xff6600,
            alpha: 1,
            gravity: 0.05,
            spread: 360
        }
    };

    constructor(effectsLayer: any, app: any) {
        this.container = effectsLayer;
        this.app = app;
        this.initPool();
        this.initGlowTexture();
    }

    /**
     * 初始化光效纹理
     */
    private initGlowTexture(): void {
        const width = 128;
        const height = 32;
        
        // 创建 canvas 绘制渐变
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
            // 径向渐变，模拟发光中心
            const gradient = ctx.createRadialGradient(
                width / 2, height / 2, 0,
                width / 2, height / 2, width / 2
            );
            gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
            gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.5)');
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
            
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, width, height);
        }

        this.glowTexture = PIXI.Texture.from(canvas);
    }

    /**
     * 初始化对象池
     */
    private initPool(size: number = 200): void {
        for (let i = 0; i < size; i++) {
            const sprite = this.createParticleSprite();
            sprite.visible = false;
            this.container.addChild(sprite);
            this.particlePool.push(sprite);
        }
    }

    /**
     * 获取光效精灵
     */
    private getGlowSprite(): any {
        let sprite: any;
        
        // 查找闲置的
        for (const s of this.glowSpritePool) {
            if (!s.visible) {
                sprite = s;
                break;
            }
        }

        // 没有则创建
        if (!sprite) {
            sprite = new PIXI.Sprite(this.glowTexture);
            sprite.anchor.set(0.5);
            sprite.blendMode = PIXI.BLEND_MODES.ADD; // 关键：叠加发光模式
            this.container.addChild(sprite);
            this.glowSpritePool.push(sprite);
        }

        sprite.visible = true;
        return sprite;
    }

    /**
     * 播放边缘发光特效 (金缮流光)
     */
    playEdgeGlowEffect(position: Vector2, rotation: number, color: number = 0xFFD700): void {
        const sprite = this.getGlowSprite();
        
        sprite.x = position.x;
        sprite.y = position.y;
        sprite.rotation = rotation; // 假设传入的角度是垂直于边的，或者沿着边的，视纹理方向而定
        // 我们的纹理是横向长条 (128x32)，所以 rotation 应该是沿着边的方向
        
        sprite.tint = color;
        sprite.alpha = 0;
        sprite.scale.set(0.5, 0.2); // 初始状态

        this.edgeGlows.push({
            sprite: sprite,
            life: 0,
            maxLife: 600 // 600ms
        });

        if (!this.isRunning) {
            this.startUpdate();
        }
    }

    /**
     * 播放轮廓流光特效 (Contour Glow)
     * @param points 贝塞尔曲线控制点数组 (屏幕坐标)
     * @param color 颜色
     */
    playContourGlow(points: Vector2[], color: number = 0xFFD700): void {
        const graphics = new PIXI.Graphics();
        graphics.lineStyle({
            width: 4,
            color: color,
            alpha: 1,
            cap: PIXI.LINE_CAP.ROUND,
            join: PIXI.LINE_JOIN.ROUND
        });
        graphics.blendMode = PIXI.BLEND_MODES.ADD;

        // 绘制贝塞尔曲线
        // EdgeGenerator 生成的点数组长度为 10 (1个起点 + 3组*3个控制点)
        if (points.length >= 4) {
            graphics.moveTo(points[0].x, points[0].y);
            
            for (let i = 1; i < points.length; i += 3) {
                if (i + 2 < points.length) {
                    graphics.bezierCurveTo(
                        points[i].x, points[i].y,
                        points[i+1].x, points[i+1].y,
                        points[i+2].x, points[i+2].y
                    );
                }
            }
        } else if (points.length === 2) {
            // 直线情况
            graphics.moveTo(points[0].x, points[0].y);
            graphics.lineTo(points[1].x, points[1].y);
        }

        this.container.addChild(graphics);

        this.edgeGlows.push({
            sprite: graphics, // Graphics 也是 DisplayObject，可以直接复用 edgeGlows 结构
            life: 0,
            maxLife: 800 // 800ms
        });

        if (!this.isRunning) {
            this.startUpdate();
        }
    }

    /**
     * 创建粒子精灵
     */
    private createParticleSprite(): any {
        // 创建圆形纹理
        const graphics = new PIXI.Graphics();
        graphics.beginFill(0xffffff);
        graphics.drawCircle(0, 0, 4);
        graphics.endFill();

        const texture = this.app.renderer.generateTexture(graphics);
        const sprite = new PIXI.Sprite(texture);
        sprite.anchor.set(0.5);

        graphics.destroy();

        return sprite;
    }

    /**
     * 从对象池获取粒子
     */
    private getParticle(): any | null {
        for (const sprite of this.particlePool) {
            if (!sprite.visible) {
                sprite.visible = true;
                return sprite;
            }
        }
        return null;
    }

    /**
     * 释放粒子回对象池
     */
    private releaseParticle(particle: Particle): void {
        particle.sprite.visible = false;
        const index = this.particles.indexOf(particle);
        if (index > -1) {
            this.particles.splice(index, 1);
        }
    }

    /**
     * 播放效果
     */
    play(type: ParticleEffectType, position: Vector2, customConfig?: Partial<ParticleConfig>): void {
        const config = { ...this.defaultConfigs[type], ...customConfig };
        this.emit(position, config);

        if (!this.isRunning) {
            this.startUpdate();
        }
    }

    /**
     * 播放边缘闪烁效果
     */
    playEdgeGlow(pathPoints: Vector2[], color: number = 0x00ff88): void {
        // 沿着边缘发射粒子
        const config: ParticleConfig = {
            count: 2,
            lifetime: 400,
            speed: 1,
            size: 3,
            color: color,
            alpha: 1,
            gravity: 0,
            spread: 90
        };

        // 沿路径均匀分布
        const step = Math.max(1, Math.floor(pathPoints.length / 15));

        for (let i = 0; i < pathPoints.length; i += step) {
            const point = pathPoints[i];
            this.emit(point, config);
        }

        if (!this.isRunning) {
            this.startUpdate();
        }
    }

    /**
     * 发射粒子
     */
    private emit(position: Vector2, config: ParticleConfig): void {
        for (let i = 0; i < config.count; i++) {
            const sprite = this.getParticle();
            if (!sprite) continue;

            // 随机角度
            const angle = (Math.random() * config.spread - config.spread / 2) * Math.PI / 180;
            const baseAngle = -Math.PI / 2;  // 向上
            const finalAngle = baseAngle + angle;

            // 随机速度
            const speed = config.speed * (0.5 + Math.random() * 0.5);

            // 设置粒子属性
            sprite.x = position.x + (Math.random() - 0.5) * 10;
            sprite.y = position.y + (Math.random() - 0.5) * 10;
            sprite.scale.set(config.size / 8);
            sprite.tint = config.color;
            sprite.alpha = config.alpha;

            const particle: Particle = {
                sprite,
                vx: Math.cos(finalAngle) * speed,
                vy: Math.sin(finalAngle) * speed,
                life: config.lifetime,
                maxLife: config.lifetime,
                gravity: config.gravity,
                fadeRate: 1 / config.lifetime
            };

            this.particles.push(particle);
        }
    }

    /**
     * 开始更新循环
     */
    private startUpdate(): void {
        this.isRunning = true;
        this.app.ticker.add(this.update, this);
    }

    /**
     * 停止更新循环
     */
    private stopUpdate(): void {
        this.isRunning = false;
        this.app.ticker.remove(this.update, this);
    }

    /**
     * 更新粒子
     */
    private update = (delta: number): void => {
        const dt = delta * 16.67;  // 转换为毫秒

        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];

            // 更新生命周期
            p.life -= dt;

            if (p.life <= 0) {
                this.releaseParticle(p);
                continue;
            }

            // 更新速度（重力）
            p.vy += p.gravity;

            // 更新位置
            p.sprite.x += p.vx * delta;
            p.sprite.y += p.vy * delta;

            // 更新透明度
            p.sprite.alpha = (p.life / p.maxLife);

            // 更新缩放（逐渐变小）
            const scale = (p.life / p.maxLife) * p.sprite.scale.x;
            p.sprite.scale.set(Math.max(0.1, scale));
        }

        // 更新边缘光效
        for (let i = this.edgeGlows.length - 1; i >= 0; i--) {
            const glow = this.edgeGlows[i];
            glow.life += dt;

            const progress = glow.life / glow.maxLife;

            if (progress >= 1) {
                glow.sprite.visible = false;
                this.edgeGlows.splice(i, 1);
                continue;
            }

            // 动画曲线
            // 0 -> 0.2: 快速变亮，拉伸
            // 0.2 -> 1.0: 缓慢变暗，变细
            
            if (progress < 0.2) {
                // Fade in & Stretch
                const t = progress / 0.2;
                glow.sprite.alpha = t;
                glow.sprite.scale.x = 0.5 + t * 0.5; // 0.5 -> 1.0 (length)
                glow.sprite.scale.y = 0.2 + t * 0.8; // 0.2 -> 1.0 (thickness flash)
            } else {
                // Fade out & Thin
                const t = (progress - 0.2) / 0.8;
                glow.sprite.alpha = 1 - t;
                glow.sprite.scale.x = 1.0 + t * 0.2; // 1.0 -> 1.2 (slightly longer)
                glow.sprite.scale.y = 1.0 - t * 0.9; // 1.0 -> 0.1 (thinner)
            }
        }

        // 如果没有活跃粒子，停止更新
        if (this.particles.length === 0 && this.edgeGlows.length === 0) {
            this.stopUpdate();
        }
    };

    /**
     * 播放烟花效果（游戏完成时）
     */
    playFireworks(centerX: number, centerY: number): void {
        const colors = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff];

        // 多次爆炸
        for (let i = 0; i < 5; i++) {
            setTimeout(() => {
                const x = centerX + (Math.random() - 0.5) * 300;
                const y = centerY + (Math.random() - 0.5) * 300;
                const color = colors[Math.floor(Math.random() * colors.length)];

                this.play(ParticleEffectType.GameComplete, { x, y }, {
                    color,
                    count: 50,
                    speed: 6,
                    lifetime: 1500
                });
            }, i * 300);
        }
    }

    /**
     * 清空所有粒子
     */
    clear(): void {
        for (const p of this.particles) {
            p.sprite.visible = false;
        }
        this.particles = [];

        for (const g of this.edgeGlows) {
            g.sprite.visible = false;
        }
        this.edgeGlows = [];

        if (this.isRunning) {
            this.stopUpdate();
        }
    }

    /**
     * 销毁
     */
    destroy(): void {
        this.clear();

        for (const sprite of this.particlePool) {
            sprite.destroy({ texture: true });
        }
        this.particlePool = [];

        for (const sprite of this.glowSpritePool) {
            sprite.destroy(); // Texture is shared (glowTexture), managed separately or let Pixi handle it
        }
        this.glowSpritePool = [];
        
        if (this.glowTexture) {
            this.glowTexture.destroy(true);
            this.glowTexture = null;
        }
    }
}
